import { useLayoutEffect, useRef } from 'react'
import { Icon } from '../../ui/Icon'
import { markKeywords } from '../../ui/markKeywords'
import { stepSearch, useSearch } from './searchStore'
import { keywordsOf } from './useCleanSearch'

/**
 * The row above the composer while searching: how many items hold the keyword and
 * which one is shown, a step older, a step newer, and the way out.
 *
 * Its own component so that App — which must not re-render per keystroke — never
 * subscribes to the query. Every button preventDefaults mousedown, the composer's own
 * rule (§5.1): the keyword box keeps the keyboard while the reader steps through hits.
 */
export function SearchStrip({ onDone }: { onDone: () => void }) {
  const s = useSearch()
  /*
   * SAY WHAT WAS SEARCHED — owner, 2026-09-28: the keyword now goes to the agent's
   * machine, which reads the whole conversation. When it cannot (an older edge, a
   * machine that did not answer), the loaded blocks are searched instead, and the
   * label says so rather than letting "No matches" mean "not in the conversation".
   */
  const where = s.scope === 'conversation' ? 'in this conversation' : 'in the loaded blocks'
  const label =
    s.scope === 'pending'
      ? 'Searching this conversation…'
      : s.count
        ? `${s.current + 1} / ${s.count}` + (s.total > s.count ? ` · newest ${s.count} of ${s.total}` : '') +
          (s.scope === 'loaded' ? ' · loaded blocks' : '')
        : s.query.trim()
          ? `No matches ${where}`
          : 'Search this conversation'
  const hold = (e: React.MouseEvent) => e.preventDefault()
  /*
   * A MATCH THE VIEW CANNOT LOAD is shown by its text — the edge serves only the newest
   * blocks of a long conversation. The text is a window of up to 4000 characters around
   * the keyword, so the panel opens scrolled to the first mark rather than to words the
   * reader did not search for (2026-09-28: "yy-mega8" sat below the panel's fold).
   */
  const olderText = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const box = olderText.current
    const mark = box?.querySelector('mark')
    if (!box || !mark) return
    box.scrollTop += mark.getBoundingClientRect().top - box.getBoundingClientRect().top - 16
  }, [s.older])
  return (
    <>
    {s.older && (
      <div id="searchOlder" role="note">
        <div className="so-head">
          Too far back to open in the view
          {s.older.ts ? ` · ${new Date(s.older.ts * 1000).toLocaleString()}` : ''}
        </div>
        <div className="so-text" ref={olderText}>
          {markKeywords(s.older.text, keywordsOf(s.query))}
        </div>
      </div>
    )}
    <div id="searchStrip" role="status" aria-live="polite">
      <Icon name="search" size={14} />
      <span className="ss-count">{label}</span>
      <button
        type="button"
        className="ss-btn"
        aria-label="Older match"
        title="Older match (Enter)"
        disabled={!s.count}
        onMouseDown={hold}
        onClick={() => stepSearch(-1)}
      >
        <Icon name="arrow_upward" size={16} />
      </button>
      <button
        type="button"
        className="ss-btn"
        aria-label="Newer match"
        title="Newer match (Shift+Enter)"
        disabled={!s.count}
        onMouseDown={hold}
        onClick={() => stepSearch(1)}
      >
        <Icon name="arrow_downward" size={16} />
      </button>
      <button type="button" className="ss-done" title="Close search (Esc)" onMouseDown={hold} onClick={onDone}>
        Done
      </button>
    </div>
    </>
  )
}
